import * as React from "react";

import { Box, Dialog, makeStyles, Theme, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import AfterpayLogo from "client/assets/afterpay-symbol.png";
import CashAppLogo from "client/assets/cash-app-symbol.png";
import { MopButton } from "client/components";
import { useBranding, useTranslation } from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { RouteWithBase } from "client/utils";

const DEFAULT_MESSAGE = "Don’t leave just yet!";

const currentPathToTimeRemaining: {
  [key in RouteWithBase]?: string;
} = {
  "/app/lead": "Just 4 more minutes!",
  "/app/onboarding": "Just 4 more minutes!",
  "/app/onboarding/bank-info": "Just under 2 minutes!",
  "/app/onboarding/beneficial-owners": "Just 4 more minutes!",
  "/app/onboarding/company-information": "Just 4 more minutes!",
  "/app/onboarding/customer-support": "Less than a minute!",
  "/app/onboarding/store-details": "Just 4 more minutes!",
};

const getRemainingTime = (route: RouteWithBase): string =>
  currentPathToTimeRemaining[route] ?? DEFAULT_MESSAGE;

const useStyles = makeStyles((theme: Theme) => {
  const { headerOnDark } = useNormalizedBrandPaletteValues(theme);

  return {
    root: {
      maxWidth: 810,
    },
    iconButton: {
      color: theme.palette.common.white,
      cursor: "pointer",
      transition: "transform 200ms",
      "&:hover": {
        transform: "scale(1.1)",
      },
    },
    modalText: {
      fontSize: `${20 / 16}rem`,
    },
    modalHeader: {
      fontSize: `${32 / 16}rem`,
      fontWeight: theme.typography.fontWeightBold,
    },
    rightSide: {
      backgroundColor: theme.palette.common.black,
    },
    dataFactWrapper: {
      [theme.breakpoints.up("md")]: {
        maxWidth: "45%",
      },
      [theme.breakpoints.down("sm")]: {
        margin: theme.spacing(0, 3, 3),
      },
    },
    factBody: {
      color: theme.palette.common.white,
      fontSize: `${14 / 16}rem`,
    },
    factHeader: {
      fontWeight: theme.typography.fontWeightBold,
      color: headerOnDark,
    },
  };
});

const DataFact: React.FC<{
  header: string;
  body: string;
}> = ({ header, body }) => {
  const classes = useStyles();
  return (
    <Box className={classes.dataFactWrapper} mb={4}>
      <Typography variant="h4" className={classes.factHeader}>
        {header}
      </Typography>
      <Typography className={classes.factBody} variant="body2">
        {body}
      </Typography>
    </Box>
  );
};

export const ExitIntentModal: React.FC<{ open: boolean; setOpen: any }> = ({
  open,
  setOpen,
}) => {
  const classes = useStyles();
  const { brandTitle, brandKey, brandName } = useBranding();
  const t = useTranslation();
  const close = () => {
    setOpen(false);
    logAmplitudeEvent("REDESIGN_ADD_YOUR_STORE", "dismissed_pop_up");
  };
  const hasBeenOpenedOnce = React.useRef(false);

  const logo = brandKey === "CashApp" ? CashAppLogo : AfterpayLogo;

  if (open && !hasBeenOpenedOnce.current) {
    hasBeenOpenedOnce.current = true;
    logAmplitudeEvent("REDESIGN_ADD_YOUR_STORE", "opened_pop_up");
  }

  const dataFacts = [
    {
      header: "30%",
      body: t("uiDataFact1", { brandTitle }),
    },
    {
      header: "40%",
      body: t("uiDataFact2", { brandTitle }),
    },
    {
      header: "22%",
      body: t("uiDataFact3"),
    },
  ];

  return (
    <Dialog
      aria-describedby="exit intent modal"
      aria-labelledby="exit-intent-modal"
      classes={{
        paper: classes.root,
      }}
      fullWidth
      onClose={close}
      open={open}
    >
      <Box display="flex">
        <Box
          width="50%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          marginTop={40}
          mb={10}
        >
          <Box position="absolute" top={0} left={0}>
            <img src={logo} alt={`${brandName} logo`} />
          </Box>

          <Box mb={1} display="flex" maxWidth={300} flexDirection="column">
            <Box mb={2}>
              <Typography className={classes.modalHeader}>
                {getRemainingTime(location.pathname as RouteWithBase)}
              </Typography>
            </Box>
            <Box mb={4}>
              <Typography className={classes.modalText}>
                {t("uiExitIntentModalText", { brandTitle })}
              </Typography>
            </Box>
            <Box>
              <MopButton mopVariant="primary" fullWidth onClick={close}>
                Go back
              </MopButton>
            </Box>
          </Box>
        </Box>
        <Box
          width="50%"
          className={classes.rightSide}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          <Box position="absolute" top={24} right={24}>
            <CloseIcon className={classes.iconButton} onClick={close} />
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center">
            {dataFacts.map((dataFact) => (
              <DataFact key={dataFact.header} {...dataFact} />
            ))}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
